// core/action/companyActions.js
import { companyAPI } from './api';
import { toast } from 'react-toastify';

export const companyActions = {
    // Get company settings
    getSettings: async () => {
        try {
            const response = await companyAPI.getSettings();
            
            if (response.data && response.success) {
                return {
                    success: true,
                    data: response.data
                };
            } else {
                return {
                    success: false,
                    message: response.message || 'Failed to load company settings',
                    data: {
                        company_name: 'Your Company',
                        company_email: '',
                        company_phone: '',
                        company_address: '',
                        company_website: '',
                        gst_number: '',
                        pan_number: '',
                        company_logo: null,
                        company_favicon: null
                    }
                };
            }
        } catch (error) {
            console.error('Error fetching company settings:', error);
            return {
                success: false,
                message: error.message || 'Failed to load company settings',
                data: {
                    company_name: 'Your Company',
                    company_email: '',
                    company_phone: '',
                    company_address: '',
                    company_website: '',
                    gst_number: '',
                    pan_number: '',
                    company_logo: null,
                    company_favicon: null
                }
            };
        }
    },

    // Update company settings
    updateSettings: async (settingsData) => {
        try {
            const token = localStorage.getItem('auth_token');
            
            if (!token) {
                toast.error('You must be logged in to update settings');
                return {
                    success: false,
                    message: 'Authentication required'
                };
            }
            
            const response = await companyAPI.updateSettings(settingsData, token);
            
            if (response.data && response.success) {
                toast.success(response.message || 'Company settings updated successfully');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response.message || 'Failed to update company settings');
                return {
                    success: false,
                    message: response.message || 'Failed to update company settings'
                };
            }
        } catch (error) {
            console.error('Error updating company settings:', error);
            toast.error(error.message || 'Failed to update company settings');
            return {
                success: false,
                message: error.message || 'Failed to update company settings'
            };
        }
    },

    // Upload company logo
    uploadLogo: async (logoFile) => {
        try {
            const token = localStorage.getItem('auth_token');
            
            if (!token) {
                toast.error('Authentication required');
                return {
                    success: false,
                    message: 'Authentication required'
                };
            }
            
            const formData = new FormData();
            formData.append('company_logo', logoFile);
            
            const response = await companyAPI.uploadLogo(formData, token);
            
            if (response.data && response.success) {
                toast.success('Logo uploaded successfully');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response.message || 'Failed to upload logo');
                return {
                    success: false,
                    message: response.message || 'Failed to upload logo'
                };
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error(error.message || 'Failed to upload logo');
            return {
                success: false,
                message: error.message || 'Failed to upload logo'
            };
        }
    },

    // Upload favicon
    uploadFavicon: async (faviconFile) => {
        try {
            const token = localStorage.getItem('auth_token');
            
            if (!token) {
                toast.error('Authentication required');
                return {
                    success: false,
                    message: 'Authentication required'
                };
            }
            
            const formData = new FormData();
            formData.append('company_favicon', faviconFile);
            
            const response = await companyAPI.uploadFavicon(formData, token);
            
            if (response.data && response.success) {
                toast.success('Favicon uploaded successfully');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response.message || 'Failed to upload favicon');
                return {
                    success: false,
                    message: response.message || 'Failed to upload favicon'
                };
            }
        } catch (error) {
            console.error('Error uploading favicon:', error);
            toast.error(error.message || 'Failed to upload favicon');
            return {
                success: false,
                message: error.message || 'Failed to upload favicon'
            };
        }
    },

    // Delete logo
    deleteLogo: async () => {
        try {
            const token = localStorage.getItem('auth_token');
            
            if (!token) {
                toast.error('Authentication required');
                return {
                    success: false,
                    message: 'Authentication required'
                };
            }
            
            const response = await companyAPI.deleteLogo(token);
            
            if (response.data && response.success) {
                toast.success('Logo removed successfully');
                return {
                    success: true,
                    message: response.message
                };
            } else {
                toast.error(response.message || 'Failed to remove logo');
                return {
                    success: false,
                    message: response.message || 'Failed to remove logo'
                };
            }
        } catch (error) {
            console.error('Error deleting logo:', error);
            toast.error(error.message || 'Failed to remove logo');
            return {
                success: false,
                message: error.message || 'Failed to remove logo'
            };
        }
    },

    // Delete favicon
    deleteFavicon: async () => {
        try {
            const token = localStorage.getItem('auth_token');
            
            if (!token) {
                toast.error('Authentication required');
                return {
                    success: false,
                    message: 'Authentication required'
                };
            }
            
            const response = await companyAPI.deleteFavicon(token);
            
            if (response.data && response.success) {
                toast.success('Favicon removed successfully');
                return {
                    success: true,
                    message: response.message
                };
            } else {
                toast.error(response.message || 'Failed to remove favicon');
                return {
                    success: false,
                    message: response.message || 'Failed to remove favicon'
                };
            }
        } catch (error) {
            console.error('Error deleting favicon:', error);
            toast.error(error.message || 'Failed to remove favicon');
            return {
                success: false,
                message: error.message || 'Failed to remove favicon'
            };
        }
    },

    // Format company data for display
    formatCompanyData: (data) => {
        return {
            company_name: data.company_name || 'Your Company',
            company_email: data.company_email || '',
            company_phone: data.company_phone || '',
            company_address: data.company_address || '',
            company_website: data.company_website || '',
            gst_number: data.gst_number || '',
            pan_number: data.pan_number || '',
            company_logo: data.company_logo ? `/${data.company_logo}` : null,
            company_favicon: data.company_favicon ? `/${data.company_favicon}` : null,
            full_logo_path: data.company_logo ? `/${data.company_logo}` : null,
            full_favicon_path: data.company_favicon ? `/${data.company_favicon}` : null
        };
    },

    // Validate GST number (Indian format)
    validateGST: (gst) => {
        if (!gst) return true; // Empty is valid (optional)
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
        return gstRegex.test(gst);
    },

    // Validate PAN number (Indian format)
    validatePAN: (pan) => {
        if (!pan) return true; // Empty is valid (optional)
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        return panRegex.test(pan);
    },

    // Validate phone number (Indian format)
    validatePhone: (phone) => {
        if (!phone) return true; // Empty is valid (optional)
        const phoneRegex = /^[+]?[0-9]{10,13}$/;
        return phoneRegex.test(phone.replace(/\s/g, ''));
    },

    // Validate email
    validateEmail: (email) => {
        if (!email) return true; // Empty is valid (optional)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    },

    // Validate website URL
    validateWebsite: (website) => {
        if (!website) return true; // Empty is valid (optional)
        try {
            new URL(website);
            return true;
        } catch {
            return false;
        }
    }
};

export default companyActions;